import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  timer
} from 'rxjs';
import { TimelineService } from './timeline.service';
import { DeviceStoreService } from './device-store.service';
import { InstanceManagerService } from '../resources/instance-manager.service';
import { StationManagerService } from '../resources/station-manager.service';
import { EventTrackerService } from './event-tracker.service';
import dayjs from 'dayjs';
import {
  ContentToPlay,
  MediaToPlay,
  MixConfiguration,
  PlayerItemToPlay,
  PlayerMix,
  PlayerSession,
  SkipStatus,
  SteeringWheelPaths
} from '../models';
import { SessionsManagerService } from '../resources/sessions-manager.service';
import {
  HuiMediaToPlay,
  HuiPlaybackErrorEvent,
  HuiPlaybackErrorTypes,
  HuiPlaybackProgressEvent,
  HuiPlaybackStatusEvent,
  HuiPlaybackStatusTypes,
  PSNotification,
  PSNotificationTypes
} from '@hidat/huijs-player';
import { PlaybackQueueService } from './playback-queue.service';
import { PlayerEngineService } from './player-engine.service';
import { uString, Uuid } from '@hidat/huijs-common';
import {
  PlayerAutoPlayMode,
  PlayerAutoPlayModes,
  SessionErrors,
  SteeringMode
} from '@splixer/common';

enum GotoNextSource {
  CONTENT_LOADED = 1,
  SEGMENT_SKIPPED,
  MIX_SKIPPED,
  MIX_PLAYED_OUT,
}

/**
 * Playback Service
 * Orchestrates the interface between the audio player and the UI and backend.
 * 12/21/2023 - This service is too big!!!
 */
@Injectable({ providedIn: 'root' })
export class PlaybackService implements OnDestroy {
  // Services
  private session = inject(SessionsManagerService);
  private playerEngine = inject(PlayerEngineService);
  private timelineService = inject(TimelineService);
  private deviceStore = inject(DeviceStoreService);
  private instance = inject(InstanceManagerService);
  private station = inject(StationManagerService);
  private eventTracker = inject(EventTrackerService); // Need to define service....
  private playbackQueue = inject(PlaybackQueueService);

  // Members
  protected mixConfig: MixConfiguration = new MixConfiguration();
  protected newMixLoading = false;
  protected newMixLoaded = false;
  protected firstMixLoaded = false; // Set to true once a mix has been loaded
  protected showPlayOverlay = false;
  protected handlingOutOfContent = false;
  public skipCount = 0;
  protected sessionError: SessionErrors | undefined;
  protected restrictedAccessMode = false;

  // Currently playing mix
  public currentMix = signal<PlayerMix | undefined>(undefined);
  public currentItemPlaying = signal<PlayerItemToPlay | undefined>(undefined);
  public relatedMixes = signal<PlayerMix[] | undefined>(undefined); // Assigned at the mix level

  //private readonly kickstartFile = 'https://s3.amazonaws.com/open.splixer.com/silence.mp3';
  private currentContentPlaying: ContentToPlay | undefined;
  private retrievingNextTracks = false;
  private kickstartStatus = 0;
  private loadErrorCount = 0;
  private skipEnabled = true;
  private canSkipAt: dayjs.Dayjs | undefined;
  private autoPlayMode = PlayerAutoPlayModes.OFF; // Beginning thinkings about what to do when the scheduler runs out of content
  private autoPlayStartedAt: dayjs.Dayjs | undefined;
  private autoPlayMaxTime = 60 * 4; // Four hours of autoplay max

  // Available Subscription Sources
  public readonly steeringWheelChangedSource = new Subject<
    PlayerMix | undefined
  >();
  public readonly contentPathChangedSource = new Subject<PlayerMix>();
  public readonly notifier = new Subject<PSNotification>();
  // Set to true the first time something starts playing - useful for hiding startup messages....
  public readonly firstPlayStarted = signal(false);

  // Player Subscriptions
  private playbackStatusSubscription$: Subscription | undefined;
  private playbackErrorSubscription$: Subscription | undefined;
  private contentStatusSubscription$: Subscription | undefined;
  private playbackProgressSubscription$: Subscription | undefined;
  private intervalTimerSubscription$: Subscription | undefined;

  // Listen Time spent Tracking
  private ltFirstStatusSeconds = 1; // Send first status after...
  private ltSecondStatusSeconds = 9; // Send second status after an additional...
  private ltThirdStatusSeconds = 20; // Send third status after an additional...
  private ltFourthStatusSeconds = 90; // Send all the remaining status every...

  private ltReportingSeconds = this.ltFirstStatusSeconds;
  private ltMaxChange = 0.8; // If change in position is greater than this, then we are probably skipping around and reset
  private ltLastPosition = 0;
  private ltSecondsSinceLastStatus = 0;
  private ltMediaItemCount = 1;

  constructor() {
    this.currentMix.set(undefined);
    this.autoPlayMode = this.station.defaultAutoPlayMode;
    this.setupSubscriptions();
  }

  public ngOnDestroy() {
    this.playbackStatusSubscription$?.unsubscribe();
    this.playbackErrorSubscription$?.unsubscribe();
    this.contentStatusSubscription$?.unsubscribe();
    this.playbackProgressSubscription$?.unsubscribe();
    this.intervalTimerSubscription$?.unsubscribe();
  }

  protected setupSubscriptions() {
    this.playbackStatusSubscription$ =
      this.playerEngine.playbackStatusSource.subscribe((playbackStatus) => {
        this.playbackStatusUpdated(playbackStatus);
      });

    this.playbackErrorSubscription$ =
      this.playerEngine.playbackErrorSource.subscribe((error) => {
        this.playbackErrorEncountered(error);
      });

    this.playbackProgressSubscription$ =
      this.playerEngine.playbackProgressSource.subscribe((pbProgress) => {
        this.playbackProgressUpdated(pbProgress);
        this.checkIfContentChanged(pbProgress);
      });

    this.intervalTimerSubscription$ = timer(5000, 5000).subscribe(() => {
      this.updateState();
    });
  }

  private playbackStatusUpdated(playbackStatus: HuiPlaybackStatusEvent) {
    const mediaToPlay = playbackStatus.source as MediaToPlay;
    if (playbackStatus.eventID == HuiPlaybackStatusTypes.STARTED) {
      this.showPlayOverlay = false;
      if (this.kickstartStatus == 1) {
        this.kickstartStatus = 2;
      }
      if (!this.firstPlayStarted()) {
        this.firstPlayStarted.set(true);
        this.notifier.next({
          type: PSNotificationTypes.FIRST_MIX_STARTED,
          message: 'Content started playing.'
        });
      }
      if (this.newMixLoaded) {
        this.notifier.next({
          type: PSNotificationTypes.MIX_STARTED,
          message: 'Content started playing.'
        });
        this.newMixLoaded = false;
        this.handlingOutOfContent = false;
      }
    } else if (playbackStatus.eventID == HuiPlaybackStatusTypes.FINISHED) {
      //console.debug('Playback Finished: ', this.ltSecondsSinceLastStatus)
      // Update listen tracking
      this.updateListeningTime();
      this.onContentChanged(
        undefined,
        this.currentContentPlaying,
        mediaToPlay,
        false
      );

      if (this.kickstartStatus == 2) {
        this.kickstartStatus = 3;
        // Only start playback if we have content waiting, as we could have content in-flight
        if (this.playbackQueue.hasMediaToPlay()) {
          this.gotoNextMediaToPlay(GotoNextSource.CONTENT_LOADED);
        }
      } else if (mediaToPlay.lastItem) {
        this.outOfContent(null, GotoNextSource.MIX_PLAYED_OUT);
      } else {
        this.gotoNextMediaToPlay(GotoNextSource.CONTENT_LOADED);
      }
    } else if (playbackStatus.eventID == HuiPlaybackStatusTypes.FILE_LOADED) {
      //-- Reset the load error count since we have successfully loaded a file. --//
      this.loadErrorCount = 0;
      // Update listen tracking
      this.ltMediaItemCount = 1;
      this.ltSecondsSinceLastStatus = 0;
      this.ltLastPosition = 0;
      this.ltReportingSeconds = this.ltFirstStatusSeconds;
      const mediaItem = playbackStatus.source as MediaToPlay;
      if (mediaItem.schedulingItem) {
        const si = this.currentItemPlaying();
        if (!si || si.uid !== mediaItem.schedulingItem.uid) {
          this.currentItemPlaying.set(mediaItem.schedulingItem);
          this.notifier.next({
            type: PSNotificationTypes.SCHEDULING_ITEM_CHANGED,
            message: 'New Scheduling Item: ' + mediaItem.schedulingItem.uid
          });
        }
      }
    }
  }

  private playbackProgressUpdated(pbProgress: HuiPlaybackProgressEvent) {
    // Are we moving forward in time
    if (pbProgress.position > this.ltLastPosition) {
      const change = pbProgress.position - this.ltLastPosition;
      if (change < this.ltMaxChange) {
        this.ltSecondsSinceLastStatus += change;
      }
    }
    this.ltLastPosition = pbProgress.position;
    if (this.ltSecondsSinceLastStatus > this.ltReportingSeconds) {
      //console.debug('Listen Time: ', this.ltSecondsSinceLastStatus);
      this.ltMediaItemCount++;
      if (this.ltMediaItemCount === 2) {
        this.ltReportingSeconds = this.ltSecondStatusSeconds;
      } else if (this.ltMediaItemCount === 3) {
        this.ltReportingSeconds = this.ltThirdStatusSeconds;
      } else if (this.ltMediaItemCount === 4) {
        this.ltReportingSeconds = this.ltFourthStatusSeconds;
      }
      this.updateListeningTime();
    }
  }

  /**
   * Checks if the currently playing content has changed.
   * This is called from the Player Progress callback, so every couple of seconds.
   * @param pbProgress
   * @private
   */
  private checkIfContentChanged(pbProgress: HuiPlaybackProgressEvent) {
    // Check if playing content has changed
    if (pbProgress.source) {
      const mediaToPlay = pbProgress.source as MediaToPlay;
      const newContentToPlay = mediaToPlay.getContentPlayingAt(
        pbProgress.position
      ) as ContentToPlay;
      if (newContentToPlay) {
        if (!this.currentContentPlaying) {
          // First Content Started
          this.currentContentPlaying = newContentToPlay;
          this.currentContentPlaying.startedAt = dayjs();

          this.onContentChanged(
            this.currentContentPlaying,
            undefined,
            mediaToPlay,
            false
          );
        } else if (newContentToPlay.uid !== this.currentContentPlaying.uid) {
          // Content Updated
          this.currentContentPlaying.finishedAt = dayjs();
          newContentToPlay.startedAt = dayjs();

          if (newContentToPlay) {
            this.onContentChanged(
              newContentToPlay,
              this.currentContentPlaying,
              mediaToPlay,
              true
            );
          }
          this.currentContentPlaying = newContentToPlay;
        }
      } else {
        // Content Finished
        if (this.currentContentPlaying) {
          console.debug(this.currentContentPlaying.uid, ' finished playing.');
          this.currentContentPlaying.finishedAt = dayjs();
          this.onContentChanged(
            undefined,
            this.currentContentPlaying,
            mediaToPlay,
            false
          );
          this.currentContentPlaying = undefined;
        }
      }
    }
  }

  private playbackErrorEncountered(error: HuiPlaybackErrorEvent) {
    console.log('Error: ' + error.errorType + ' - ' + error.message);
    if (error.errorType === HuiPlaybackErrorTypes.LOAD) {
      if (error.message == '4' && this.loadErrorCount == 0) {
        this.loadErrorCount++;
      } else {
        console.log('Load Error, skipping: ' + this.loadErrorCount);
        // Mark the bad content as played so we don't try to play it again
        /*
        if (error.contentToPlay) {
          const contentToPlay = error.contentToPlay as ContentToPlay;
          this.session.markContentChanged(this.deviceStore.sessionId, undefined, contentToPlay.id, false);
        }*/

        this.loadErrorCount++;
        if (this.loadErrorCount < 10) {
          this.gotoNextMediaToPlay(GotoNextSource.CONTENT_LOADED, true);
        } else {
          this.notifier.next({
            type: PSNotificationTypes.LOAD_ERROR,
            message: 'Unable to find tracks to play.'
          });
        }
      }
    } else if (error.errorType === HuiPlaybackErrorTypes.PLAYBACK) {
      this.notifier.next({
        type: PSNotificationTypes.PLAYBACK_ERROR,
        message:
          'Your device stopped playback from starting, please press the play button to start playback.'
      });
    } else {
      this.notifier.next({
        type: PSNotificationTypes.INFO,
        message: 'There was an error encountered.'
      });
    }
  }

  private updateListeningTime() {
    this.session
      .trackListeningTime(
        this.deviceStore.sessionId,
        this.ltSecondsSinceLastStatus
      )
      .subscribe();
    this.ltSecondsSinceLastStatus = 0;
  }

  /**
   * Toggles between play and pause. Fires an analytics event via `EventTrackerService`.
   * @param eventSource - Label passed to the event tracker identifying what triggered the toggle (e.g. `'play_button'`).
   */
  public togglePlayback(eventSource: string) {
    if (this.playerEngine.paused() || this.playerEngine.playbackStartError()) {
      this.playerEngine.resume();
      if (eventSource) {
        this.eventTracker.playResumed(eventSource);
      }
    } else {
      this.playerEngine.pause();
      if (eventSource) {
        this.eventTracker.playPaused(eventSource);
      }
    }
  }

  // Called when we have detected the current content item has changed.
  private onContentChanged(
    contentToPlay: ContentToPlay | undefined,
    contentPlayed: ContentToPlay | undefined,
    mediaToPlay: MediaToPlay | undefined,
    getNewAltPaths: boolean
  ): void {
    const contentToPlayId = contentToPlay?.uid;
    const contentPlayedId = contentPlayed?.uid;
    if (contentToPlay) {
      this.timelineService.addCurrentlyPlayingItem(
        contentToPlay,
        mediaToPlay,
        this.currentMix()
      );
    }
    this.updateState();
    if (contentToPlay || contentPlayed) {
      this.session
        .markContentChanged(
          this.deviceStore.sessionId,
          contentToPlayId,
          contentPlayedId,
          getNewAltPaths
        )
        .subscribe(ds => {
          // Check if the session id has changed
          const session = ds.firstItem();
          if (session) {
            this.addServerResults(session, false);
          }
        });
    }
  }

  /** `true` while the audio engine is actively playing (not paused, not stopped). */
  public isPlaying = computed<boolean>(() => {
    return this.playerEngine.playing();
  });

  /** `true` when the engine is playing but currently paused (i.e. mid-playback pause). */
  public isPlaybackPaused = computed<boolean>(() => {
    return this.playerEngine.playing() && this.playerEngine.paused();
  });

  /**
   * Starts playback of the given MediaItem
   * @param mediaItem
   * @protected
   */
  protected play(mediaItem: MediaToPlay) {
    const pb = mediaItem.playback;
    if (pb?.url) {
      let expiresAt: Date | undefined;
      if (pb.expiresAt) {
        expiresAt = pb.expiresAt.toDate();
      }
      const ttp = new HuiMediaToPlay(pb.url, mediaItem, pb.duration, expiresAt);
      this.playerEngine.play(ttp);
    }
  }

  /**
   * Returns `true` if the user is allowed to skip right now.
   * Governed by `skipEnabled`, which respects SoundExchange skip limits and a cooldown timer.
   */
  public canSkip() {
    return this.skipEnabled; //|| this.debugMode();
  }

  /**
   * Sets the behaviour when the queue runs out of content.
   * @param newMode - `OFF` stops playback; `RANDOM_SHOW` picks a related show automatically (capped at 4 hours).
   */
  public setAutoPlayMode(newMode: PlayerAutoPlayMode) {
    this.autoPlayMode = newMode;
  }

  /**
   * Notifies the server that the user has skipped the current track.
   * This is a fire and forget routine, we don't wait for the results to return.
   * @protected
   */
  protected trackSkip(): void {
    if (this.currentContentPlaying) {
      // If we are currently playing, lets update the session before we switch playlists
      if (this.ltLastPosition > 0 && this.ltSecondsSinceLastStatus > 10) {
        this.updateListeningTime();
      }

      this.session
        .contentSkipped(
          this.deviceStore.sessionId,
          this.currentContentPlaying.uid
        )
        .subscribe((ds) => {
          if (ds) {
            const session = ds.firstItem();
            if (session) {
              this.addServerResults(session, false);
              // @TDOD this.setSkipStatus(session._skip_status;
              this.eventTracker.contentSkipped();
            }
          }
        });
    }
  }

  /**
   * Skips to the next audio file in the queue and notifies the server.
   * Emits `OUT_OF_SKIPS` if skipping is not currently allowed.
   * @param forceSkip - Bypass the `canSkip()` guard (e.g. for admin or automated skips).
   */
  public skipSegment(forceSkip = true) {
    if (this.canSkip() || forceSkip) {
      this.trackSkip();
      this.gotoNextMediaToPlay(GotoNextSource.SEGMENT_SKIPPED, true);
    } else {
      const msg = 'Skip not allowed at this time.';
      this.notifier.next({
        type: PSNotificationTypes.OUT_OF_SKIPS,
        message: msg
      });
      console.warn(msg);
    }
  }

  /**
   * Skips to the first audio file of the next scheduling item (show or segment).
   * Notifies the server and fires an analytics event.
   */
  /* Dead?
  public skipToNextItem() {
    this.trackSkip();
    this.gotoNextItemToPlay();
  }*/

  /**
   * Jumps to an already scheduled item based on the given ScheduleLog UID.
   * Used primarily by the Steering Wheel.
   * @param scheduleLogUid - UID of the schedule log entry to jump to.
   * @returns `Observable<boolean>` — `true` on success, `false` if no active instance.
   */
  public playScheduledItem(scheduleLogUid: Uuid): Observable<boolean> {
    if (this.instance.instanceId) {
      this.trackSkip();
      const p = this.session.gotoScheduledItem(
        this.instance.instanceId,
        this.deviceStore.sessionId,
        scheduleLogUid
      );
      return this.handlePlayStartResults(p);
    }
    return of(false);
  }

  /**
   * Starts playback of the given mix identified by its UID.
   * Creates a session if none exists, otherwise switches the existing session to the new path.
   * Emits `MIX_LOADING` immediately and `MIX_LOADED` / `LOAD_ERROR` when the server responds.
   * @param mixUid - UID of the mix to play.
   * @param eventSource - Label passed to the event tracker (e.g. `'station_picker'`).
   * @returns `Observable<boolean>` — `true` when the queue is loaded and playback starts.
   */
  public playMix(
    mixUid: string,
    eventSource: string
  ): Observable<boolean> {
    this.restrictedAccessMode = true; // This isn't really true, but it's the easy way out right now
    if (this.deviceStore.hasInstance() && this.deviceStore.hasSession()) {
      this.notifier.next({
        type: PSNotificationTypes.MIX_LOADING,
        message: 'Loading Mix.'
      });
      this.newMixLoading = true;
      this.trackSkip();
      this.autoPlayStartedAt = undefined;
      this.eventTracker.mixStarted(eventSource);
      const p = this.session.playMix(
        this.deviceStore.instanceId,
        this.deviceStore.sessionId,
        this.station.stationCode,
        mixUid
      );
      return this.handlePlayStartResults(p);
    } else {
      // MIX_LOADING called in upsert
      return this.upsertSession(undefined, mixUid, undefined, true);
    }
  }

  /**
   * Repositions playback to a specific `MediaToPlay` already in the queue.
   * Assumes the item belongs to the current scheduling item.
   * @param item - The media item to seek to; must be present in the current queue.
   * @returns `true` if the item was found and playback started; `false` otherwise.
   */
  public playQueuedMediaItem(item: MediaToPlay): boolean {
    if (this.playbackQueue.gotoMediaItem(item)) {
      this.gotoNextMediaToPlay(GotoNextSource.SEGMENT_SKIPPED, true);
      return true;
    }
    return false;
  }

  /**
   * Updates the can_skip, can_skip_at, and skip_count from the given hash
   * @param skipStatus
   */
  private setSkipStatus(skipStatus: SkipStatus) {
    if (skipStatus) {
      this.skipEnabled = skipStatus.canSkip;
      this.skipCount = skipStatus.count;
      if (skipStatus.canSkipAt) {
        this.canSkipAt = skipStatus.canSkipAt;
      }
    }
  }

  /**
   * Goto Next Media Item
   * Attempts to move to the next media item to play.
   */
  protected gotoNextMediaToPlay(
    source: GotoNextSource,
    startPlaying?: boolean
  ) {
    // Make sure we have loaded a mix
    if (!this.firstMixLoaded) {
      return;
    }

    let mediaAddedToPlayer = false;
    let lastItemStarted = false;
    console.log('Going to next media item');
    if (this.playbackQueue.hasMediaToPlay()) {
      const mtp = this.playbackQueue.getNextMediaItem();
      if (
        mtp &&
        mtp.playback &&
        mtp.playback.url &&
        mtp.playback.url.length > 0
      ) {
        lastItemStarted = mtp.lastInItem;
        this.play(mtp);
        mediaAddedToPlayer = true;
      }
    }

    // Let's see if we just used the last segment, and if we did, kickoff load some more
    if (this.playbackQueue.needsMediaToPlay() && !lastItemStarted) {
      // If we aren't in a hurry, lets delay for a couple of seconds to reduce any gap in playback....
      if (mediaAddedToPlayer) {
        // Change to RXJS timer so we can cancel functionally?
        setTimeout(() => {
          this.getNextTracksFromServer(GotoNextSource.CONTENT_LOADED, false);
        }, 5000);
      } else {
        // We are out, panic!
        this.getNextTracksFromServer(source, startPlaying);
      }
    }
  }

  /**
   * Goes to the next SCHEDULING item to play.
   * If there are none, attempts to get the next item and starts it playing...
   * Need to deal with non-continuous mixes....
   * @protected
   */
  /* Dead?
  protected gotoNextItemToPlay() {
    // Make sure we have loaded a mix
    if (!this.firstMixLoaded) {
      return;
    }

    let mediaAddedToPlayer = false;
    if (this.playbackQueue.hasItemToPlay()) {
      const mtp = this.playbackQueue.getNextItemToPlay();
      if (
        mtp &&
        mtp.playback &&
        mtp.playback.url &&
        mtp.playback.url.length > 0
      ) {
        this.play(mtp);
        mediaAddedToPlayer = true;
      }
    }

    // Let's see if we just used the last segment, and if we did, kickoff load some more
    if (this.playbackQueue.needsItemToPlay()) {
      // If we aren't in a hurry, lets delay for a couple of seconds to reduce any gap in playback....
      this.playbackQueue.clearUnplayedItems();
      if (mediaAddedToPlayer) {
        // Change to RXJS timer so we can cancel functionally?
        setTimeout(() => {
          this.getNextTracksFromServer(GotoNextSource.CONTENT_LOADED, false);
        }, 5000);
      } else {
        // We are out, panic!
        this.getNextTracksFromServer(GotoNextSource.SEGMENT_SKIPPED, true);
      }
    }
  }*/

  /**
   * Get Next Tracks from Server
   * Asks the server for a bunch more content to play
   * @param source
   * @param startPlaying
   */
  protected getNextTracksFromServer(
    source: GotoNextSource,
    startPlaying?: boolean
  ): void {
    if (!this.retrievingNextTracks) {
      //console.info('Loading tracks: ', skipToNewTracks);
      this.session
        .getItemsToPlay(this.instance.instanceId, this.deviceStore.sessionId)
        .pipe(
          catchError((err) => {
            console.error(err);
            this.notifier.next({
              type: PSNotificationTypes.LOAD_ERROR,
              message: 'There was an error loading content.'
            });
            return of(undefined);
          }),
          finalize(() => {
            this.retrievingNextTracks = false;
          })
        )
        .subscribe((session) => {
          if (session) {
            // Lets see if we have run out of tracks
            if (session.sessionError && !(session._itemsToPlay && session._itemsToPlay.length > 0)) {
              //@TODO: Need an 'OutOfContent' error
              this.outOfContent(session, source);
            } else {
              if (startPlaying === undefined) {
                console.debug(
                  'Tracks loaded,  playing:',
                  this.playerEngine.playing()
                );
                startPlaying = !this.playerEngine.playing();
              }
              this.addServerResults(session, startPlaying);
            }
          }
          this.retrievingNextTracks = false;
        });
    }
  }

  protected outOfContent(
    session: PlayerSession | undefined | null,
    source: GotoNextSource
  ) {
    // Are we trying to skip to the next item, either because we have run out, or the user requested  it?
    if (this.playerEngine.playing() && !(source === GotoNextSource.SEGMENT_SKIPPED || source === GotoNextSource.MIX_SKIPPED)) {
      // On last segment of current mix, lets tee up another mix if enabled
      console.debug('Almost done with mix, lets find some options: ', source);
      this.notifier.next({
        type: PSNotificationTypes.ALMOST_OUT_OF_CONTENT,
        message: 'Almost out of content, you should queue something up!'
      });
    } else if (!this.handlingOutOfContent) {
      let handled = false;
      this.handlingOutOfContent = true;

      // Let's see if we have another mix queued up
      if (this.playbackQueue.hasMixQueued()) {
        const mix = this.playbackQueue.getNextMix();
        if (mix?.uid) {
          handled = true;
          this.playMix(mix.uid, 'queued_mix');
        }
      }
      if (!handled && this.autoPlayMode !== PlayerAutoPlayModes.OFF) {
        const currentMix = this.currentMix();
        if (
          this.autoPlayMode === PlayerAutoPlayModes.RANDOM_SHOW ||
          (currentMix?.sourceType === 'Show' && currentMix.broadcastedAt)
        ) {
          // Let's check if we have exceeded max autoplay time
          let canAutoPlay = true;
          const now = dayjs();
          if (
            this.autoPlayStartedAt &&
            now.diff(this.autoPlayStartedAt, 'minutes') > this.autoPlayMaxTime
          ) {
            canAutoPlay = false;
          }
          if (canAutoPlay) {
            handled = true;
            if (!this.autoPlayStartedAt) {
              this.autoPlayStartedAt = now;
            }
            this.eventTracker.mixStarted('autoplay');
            this.gotoNextMix(this.autoPlayMode);
          }
        }
      }
      if (!handled) {
        if (source === GotoNextSource.MIX_PLAYED_OUT) {
          this.notifier.next({
            type: PSNotificationTypes.MIX_PLAYED_OUT,
            message: 'Mix finished playing. Please choose a new mix!'
          });
        } else {
          this.notifier.next({
            type: PSNotificationTypes.OUT_OF_CONTENT,
            message: 'No more content found.  Please choose a new mix!'
          });
        }
      }
    } else {
      this.handlingOutOfContent = false;
    }
  }

  /**
   * Creates or Updates the session with the new filter.
   * This will either create or update the session on the server, saving the ID's to local storage, and pulling back the
   * initial array of tracks to play.
   * @param {SessionFilter} mixConfig
   * @param contentPathUid
   * @param userMixId
   * @param clearPlaylist
   * @returns {Promise<any>}
   */
  protected upsertSession(
    mixConfig?: MixConfiguration,
    contentPathUid?: string,
    userMixId?: number,
    clearPlaylist = false
  ): Observable<boolean> {
    this.notifier.next({
      type: PSNotificationTypes.MIX_LOADING,
      message: 'Loading/updating session.'
    });

    let p: Observable<PlayerSession | undefined> | undefined;
    //console.debug('starting session')
    if (this.instance.instanceId && this.deviceStore.hasSession()) {
      p = this.session.updateSession(
        this.instance.instanceId,
        this.deviceStore.sessionId,
        mixConfig,
        contentPathUid,
        userMixId
      );
    } else {
      p = this.session.createSession(
        this.station.stationCode,
        this.instance.instanceId,
        mixConfig,
        contentPathUid,
        userMixId
      );
    }
    if (p) {
      this.newMixLoading = true;
      return this.handlePlayStartResults(p, clearPlaylist);
    }
    return of(false);
  }

  /**
   * Attempts to find another mix related to the current mix, and if found, starts to play it.
   * This is meant to be called when we have run out of content to play, and there is nothing queued.
   * @protected
   */
  protected gotoNextMix(
    autoPlayMode: PlayerAutoPlayMode
  ): Observable<PlayerSession | undefined> | undefined {
    let p: Observable<PlayerSession | undefined> | undefined;
    if (this.instance.instanceId) {
      p = this.session.gotoNextMix(
        this.instance.instanceId,
        this.deviceStore.sessionId,
        autoPlayMode
      );
      this.handlePlayStartResults(p, false).subscribe();
    }
    return p;
  }

  /**
   * Handles a playback start call results, loading in the track queue and kicking off playback
   * @param p
   * @param clearPlaylist
   */
  protected handlePlayStartResults(
    p: Observable<PlayerSession | undefined>,
    clearPlaylist = true
  ): Observable<boolean> {
    return p.pipe(
      map((session) => {
        //console.debug('Handle Play Start')
        if (session) {
          if (session.sessionError) {
            this.notifier.next({
              type: PSNotificationTypes.LOAD_ERROR,
              message: 'There was an error loading the mix.'
            });
            return false;
          } else {
            if (clearPlaylist) {
              this.playbackQueue.clearUnplayedItems();
            }
            this.addServerResults(session, true);
            this.newMixLoaded = true;
            this.newMixLoading = false;
            this.notifier.next({
              type: PSNotificationTypes.MIX_LOADED,
              message: 'Mix loaded.'
            });
          }
          return true;
        } else {
          this.notifier.next({
            type: PSNotificationTypes.LOAD_ERROR,
            message: 'There was an error loading the mix.'
          });
          return false;
        }
      }),
      catchError((err) => {
        this.notifier.next({
          type: PSNotificationTypes.LOAD_ERROR,
          message: 'There was an error loading the mix.'
        });
        console.error(err);
        this.newMixLoaded = false;
        this.newMixLoading = false;
        return of(false);
      })
    );
  }

  /**
   * Adds the results from the server to the session.
   * @param session - Session results from the server.
   * @param {boolean} skipToNext - Should  the system skip to the next track as soon as items have been loaded?
   */
  protected addServerResults(session: PlayerSession, skipToNext: boolean) {
    if (session.sessionId) {
      // Check if we are starting a new instance, or the session id has changed, and update accordingly
      if (session._instance) {
        this.instance.load(session._instance);
      }

      this.deviceStore.updateSession(session.sessionId);

      this.sessionError = session.sessionError;

      // Check if we ran into an error loading mix/content
      if (session.sessionError) {
        return;
      }

      // Check if we have a new content path, if so, let the world know
      if (session._currentMix) {
        if (
          !this.currentMix() ||
          this.currentMix()?.uid != session._currentMix.uid
        ) {
          console.debug('New Content Path: ', session._currentMix.name);
          const newMix = session._currentMix;
          this.currentMix.set(newMix);
          // Only assigned related mixes when the mix changes
          if (session._relatedContent) {
            this.relatedMixes.set(session._relatedContent);
          }
          this.contentPathChangedSource.next(newMix);
        }
      }

      if (session._itemsToPlay) {
        this.playbackQueue.addItemsToQueue(session._itemsToPlay);
      } else if (session._mediaToPlay) {
        this.playbackQueue.addItemsToQueue(session._mediaToPlay);
      } else if (this.playbackQueue.needsMediaToPlay() && skipToNext) {
        this.outOfContent(session, GotoNextSource.CONTENT_LOADED);
      }

      if (session.skipStatus) {
        this.setSkipStatus(session.skipStatus);
      }

      this.firstMixLoaded = true;

      if (skipToNext) {
        this.startPlayback();
      }
    }
  }

  /**
   * Fetches alternative content paths from the server to populate the steering wheel.
   * Creates an instance/session as a side-effect if one doesn't exist yet.
   * @param steeringMode - The steering mode governing which alternatives are returned.
   * @param shuffleCount - How many times the user has shuffled; sent to the server to measure engagement.
   * @returns `Observable<SteeringWheelPaths | undefined>` — the available paths, or `undefined` on error.
   */
  public getAltPaths(
    steeringMode: SteeringMode,
    shuffleCount: number
  ): Observable<SteeringWheelPaths | undefined> {
    console.debug('get alt paths');
    let instanceId: uString;
    if (this.deviceStore.hasInstance()) {
      instanceId = this.deviceStore.instanceId;
    }
    let sessionId: uString;
    if (this.deviceStore.hasSession()) {
      sessionId = this.deviceStore.sessionId;
    }

    return this.session
      .getAltPaths(
        steeringMode,
        shuffleCount,
        this.station.stationCode,
        instanceId,
        sessionId
      )
      .pipe(
        map((session) => {
          if (session) {
            if (!instanceId && session._instance) {
              this.instance.load(session._instance);
            }
            if (!sessionId && session.sessionId) {
              this.deviceStore.updateSession(session.sessionId);
            }
            return session._altPaths;
          }
          return undefined;
        })
      );
  }

  protected startPlayback(): boolean {
    if (this.playbackQueue.hasMediaToPlay()) {
      this.gotoNextMediaToPlay(GotoNextSource.CONTENT_LOADED);
      return true;
    } else {
      this.notifier.next({
        type: PSNotificationTypes.OUT_OF_CONTENT,
        message: 'No content found to play.'
      });
      console.warn('No content found to play');
    }
    return false;
  }

  /**
   * Asks the engine to retry the current item after a `PLAYBACK_ERROR` notification.
   * Does not advance the queue — use `skipSegment()` if you want to move on instead.
   */
  public retryPlayback(): void {
    console.info('Retrying to start playback.');
    this.playerEngine.retryPlay();
  }

  /**
   * Updates the current state of the session.
   * Checks if we can skip again, and then calls the timeline's update function.
   * This is meant to be called off of a timer.
   */
  protected updateState() {
    // Check if the user can skip again
    if (!this.skipEnabled && this.canSkipAt?.isBefore(dayjs())) {
      this.skipEnabled = true;
    }
  }
}
